import type { NoteName, Scale } from '@tunasong/schemas'
import { fromName } from '../chord/library.js'
import { inferKeyCandidates, isInKey, spellInKey } from '../chord/concert-pitch.js'
import { getNoteByChromaticOffset, getNoteChromaticIndex } from '../note/note-lib.js'
import { analyzeChord } from './analyze-chord.js'
import type { ChordAnalysis, ChordFunction } from './types.js'

/**
 * Which imported chords are worth a second listen — the analysis behind the cover-song "check this
 * chord" marker (spec `docs/superpowers/specs/2026-10-18-cover-song-chord-check-design.md`, UX
 * `docs/ux/2026-10-18-cover-song-chord-check-ux.md`).
 *
 * Two consumers share one per-chord core:
 *
 * - `explainForPlausibility` — the research path's fabrication report (`validate-plausibility.ts` in
 *   ai-api). Its verdicts are pinned by a parity snapshot and must not drift.
 * - `findChordsToCheck` — the in-editor marker. Same rescues, plus Bowie's marker-only rules: parallel
 *   borrowings always explained, applied dominants explained only when they resolve, and the four
 *   chart-level gates.
 *
 * Pure and synchronous: it runs in the browser on the paste path and in the task Lambda on the
 * research path. No model, no network.
 */

/** Criteria the analysis consumes. Bowie owns every value. */
export interface PlausibilityCriteria {
  /** Research path: ratio of unexplained chords, 0..1, above which a warning is emitted. Not an acceptance bar. */
  maxNonDiatonicRatio: number
  /** Research path: arming switch. Bowie's verdict is that this metric must never reject. */
  rejectOnFailure: boolean
  /** Chord functions that do not count against the ratio even though they are not diatonic. */
  allowedFunctions?: ChordFunction[]
  /** Below this many analyzable chords, report no ratio at all (and mark nothing). */
  minAnalyzableChords?: number
}

/**
 * Bowie's criteria (2026-08-09). 0.4 is a *reporting* threshold for the research path, not a bar
 * anything is held to. The whitelist is deliberately complete.
 */
export const bowieCriteria: PlausibilityCriteria = {
  maxNonDiatonicRatio: 0.4,
  rejectOnFailure: false,
  minAnalyzableChords: 8,
  allowedFunctions: [
    // parallel-mode borrowings into major
    'i',
    'ii°',
    '♭II',
    '♭III',
    '♭III+',
    'iv',
    'v',
    '♭VI',
    '♭VII',
    // borrowings into minor
    'I',
    'ii',
    'iii',
    'IV',
    'V',
    'vi',
    'vii°',
    // applied dominants
    'V/ii',
    'V/IV',
    'V/V',
    'V/vi',
    // tritone substitutions
    'subV/I',
    'subV/ii',
    'subV/IV',
    'subV/V',
    'subV/vi',
    // modal-distinctive
    'II',
    '#iv°',
    'vii',
    'i°',
    'iii°',
    'v°',
    'vi°',
    '♭iii',
    '♭V',
    '♭vii',
  ] as ChordFunction[],
}

/** The marker's chart-level gates (Bowie, 2026-10-18, PR #2364 Q2/Q3). */
export const markerGates = {
  /** Mark nothing when more than this share of analyzable occurrences is flagged. */
  maxFlaggedRatio: 0.25,
  /** Mark nothing when more than this many distinct symbols are flagged: past it they are the style, not outliers. */
  maxDistinctFlagged: 3,
} as const

/** A chord as the importers carry it. `variant` is '' for a plain major triad; the slash bass is ignored. */
export interface ChordSymbol {
  root: string
  variant?: string
}

export type ChordCheckReason = 'chromatic' | 'unexplained-borrowing' | 'unresolved-applied-dominant'

export interface ChordToCheck {
  /** Index into the input array, so callers can map back to their own structures. */
  index: number
  reason: ChordCheckReason
  /** The function in the first candidate key, e.g. 'chromatic', '♭VI', 'V/vi'. */
  chordFunction: ChordFunction
  /** The candidate keys it was unexplained in, by name, e.g. ['C major']. */
  keys: string[]
  /** True when the keys came from inference, false when the chart stated the key. */
  inferred: boolean
  /** For `unresolved-applied-dominant` only: where it points, and what actually follows it here. */
  resolution?: { target: string; next: string }
}

export interface ChordsToCheckResult {
  /** Empty when no analysis ran (no candidate key). */
  keys: string[]
  /** Share of analyzable occurrences flagged, before the gates. Null when no analysis ran. */
  flaggedRatio: number | null
  /** Every occurrence to mark, in song order. Empty when a gate suppressed the markers. */
  chords: ChordToCheck[]
}

/** Variants with no third: nothing about them can contradict the key's quality. */
const thirdlessVariants = new Set(['5', 'sus2', 'sus4', '7sus4', '9sus4', 'maj7sus4', 'sus'])

/** Dominant-seventh family: the blues tonic and subdominant live here. */
const dominantSeventhVariants = new Set(['7', '9', '11', '13', '7#9', '7b9', '7#5', '7b5', '7sus'])

/** Major-quality triads, the form an applied dominant usually takes in a chart. */
const majorTriadVariants = new Set(['', 'maj', 'M'])

/** The name the parser reads: root plus variant, slash bass dropped (it doesn't change the function). */
const nameOf = ({ root, variant }: ChordSymbol) => `${root}${variant ?? ''}`

const analyze = (name: string, key: Scale): ChordAnalysis | null => {
  const chord = fromName(name.trim())
  return chord ? analyzeChord({ chord, scale: key }) : null
}

/** Pitch class 0–11 of a root, or undefined for anything that is not a note name. */
const pitchOf = (root: string): number | undefined => {
  const index = getNoteChromaticIndex(root as NoteName)
  return index >= 0 ? index : undefined
}

/** Not diatonic to the key: outside it entirely, or reached by borrowing. */
const isNonDiatonic = (analysis: ChordAnalysis) => analysis.chordFunction === 'chromatic' || analysis.borrowing !== null

/** Does this bare root, taken as a plain triad, belong to the key at all? */
const belongsToKey = (root: string, key: Scale, quality: '' | 'm') => {
  const analysis = analyze(`${root}${quality}`, key)
  return Boolean(analysis) && analysis?.chordFunction !== 'chromatic'
}

/** R1 — third-less: explained when the root is a note of the key or its parallel. */
const isThirdlessInKey = (root: string, variant: string, key: Scale) =>
  thirdlessVariants.has(variant) && (belongsToKey(root, key, '') || belongsToKey(root, key, 'm'))

/**
 * R2 — blues seven: a dominant-seventh-family chord whose plain major triad belongs to the key. The
 * research path's form, pinned by the parity snapshot.
 */
const isBluesSeven = (root: string, variant: string, key: Scale) =>
  dominantSeventhVariants.has(variant) && belongsToKey(root, key, '')

/**
 * R2 for the marker (Bowie, #2423 correction 1): rescues a dominant seventh only when its plain triad is
 * diatonic or a parallel-mode borrowing — blues sevenths (C7 F7 G7 in C), Dorian vamps (A7 in E minor),
 * C7 in A minor — never when the triad reads as an applied dominant. Those go on to the resolution rule,
 * since charts usually write applied dominants as sevenths. Today the analyzer labels plain triads like E
 * in C `chromatic`, so the two forms agree; this keeps them agreeing if it ever labels them `V/x`.
 */
const isMarkerBluesSeven = (root: string, variant: string, key: Scale) => {
  if (!dominantSeventhVariants.has(variant)) {
    return false
  }
  const triad = analyze(root, key)
  return Boolean(triad) && triad?.chordFunction !== 'chromatic' && triad?.borrowing !== 'secondary-dominant'
}

/**
 * R3 — applied dominant written as a plain triad: a major chord a perfect fifth above an actual,
 * non-diminished DEGREE of the key. Accepting a borrowed target over-fires (Ab in C would pass as an
 * applied dominant of the ♭II). Returns the degree it points at, e.g. 'vi', or null.
 */
const appliedDominantTarget = (root: string, variant: string, key: Scale): ChordFunction | null => {
  if (!majorTriadVariants.has(variant) || pitchOf(root) === undefined) {
    return null
  }
  const target = getNoteByChromaticOffset(root as NoteName, 5)
  for (const quality of ['', 'm']) {
    const analysis = analyze(`${target}${quality}`, key)
    if (
      analysis &&
      analysis.borrowing === null &&
      analysis.chordFunction !== 'chromatic' &&
      !analysis.chordFunction.includes('°')
    ) {
      return analysis.chordFunction
    }
  }
  return null
}

const isAppliedDominantTriad = (root: string, variant: string, key: Scale) =>
  appliedDominantTarget(root, variant, key) !== null

/**
 * R4 — ♭II in minor (Bowie, 2026-10-18): a major triad or maj7 whose root is a half step above a
 * minor key's tonic — the Phrygian cadence chord and the Neapolitan. The analyzer now labels it
 * (#2384); the rule stays as a regression guard, like R1–R3.
 */
const isMinorFlatTwo = (root: string, variant: string, key: Scale) => {
  const rootPitch = pitchOf(root)
  const tonicPitch = pitchOf(key.root)
  return (
    key.type === 'minor' &&
    (majorTriadVariants.has(variant) || variant === 'maj7') &&
    rootPitch !== undefined &&
    tonicPitch !== undefined &&
    rootPitch === (tonicPitch + 1) % 12
  )
}

/**
 * The research path's rescues: R1 and R2. R3 is separate, because for the marker an applied dominant
 * must still resolve. The marker uses R1, its own narrower R2 and R4. R4 is marker-only: it is new since
 * the research path was tuned, and on a ♭IImaj7 in minor (which the analyzer's ♭II check doesn't cover)
 * it would change that path's verdicts, which the parity snapshot forbids.
 */
const isRescued = (root: string, variant: string, key: Scale) =>
  isThirdlessInKey(root, variant, key) || isBluesSeven(root, variant, key)

export type PlausibilityVerdict =
  { kind: 'unanalyzable' } | { kind: 'explained' } | { kind: 'unexplained'; chordFunction: ChordFunction }

/**
 * The research path's verdict on one chord: non-diatonic, not rescued (R1–R3), and not on the
 * whitelist. Pinned by the parity snapshot in ai-api.
 */
export const explainForPlausibility = (
  chord: ChordSymbol,
  key: Scale,
  criteria: PlausibilityCriteria
): PlausibilityVerdict => {
  const analysis = analyze(nameOf(chord), key)
  if (!analysis) {
    return { kind: 'unanalyzable' }
  }
  if (!isNonDiatonic(analysis)) {
    return { kind: 'explained' }
  }
  const variant = chord.variant ?? ''
  if (isRescued(chord.root, variant, key) || isAppliedDominantTriad(chord.root, variant, key)) {
    return { kind: 'explained' }
  }
  if ((criteria.allowedFunctions ?? []).includes(analysis.chordFunction)) {
    return { kind: 'explained' }
  }
  return { kind: 'unexplained', chordFunction: analysis.chordFunction }
}

const keyNameOf = (key: Scale) => `${key.root} ${key.type ?? 'major'}`

/** The applied-dominant reading of a chord, if it has one: a `V/x` function, or R3's plain-triad case. */
const appliedDominantFunction = (analysis: ChordAnalysis, chord: ChordSymbol, key: Scale): ChordFunction | null => {
  if (analysis.borrowing === 'secondary-dominant' && analysis.chordFunction.startsWith('V/')) {
    return analysis.chordFunction
  }
  const target =
    analysis.chordFunction === 'chromatic' ? appliedDominantTarget(chord.root, chord.variant ?? '', key) : null
  // The analyzer's own label set doesn't name every applied dominant (V/iii in C, minor-key V/x before
  // #2383); R3's target degree does, so the copy can still say what it reads as.
  return target ? (`V/${target}` as ChordFunction) : null
}

const isSameSymbol = (a: ChordSymbol, b: ChordSymbol) =>
  pitchOf(a.root) !== undefined && pitchOf(a.root) === pitchOf(b.root) && (a.variant ?? '') === (b.variant ?? '')

/**
 * Bowie's resolution rule (Q1): an applied dominant is explained when the next chord in song order is
 * its target (a root a perfect 5th below, any quality), a deceptive resolution (a root a whole or half
 * step above), another applied dominant (a chain), or the same chord repeated. The last chord of the
 * song has no next chord and counts as explained.
 */
const isResolved = (chords: readonly ChordSymbol[], index: number, key: Scale) => {
  const chord = chords[index]
  const next = chords[index + 1]
  if (!next) {
    return true
  }
  const root = pitchOf(chord.root)
  const nextRoot = pitchOf(next.root)
  if (root === undefined || nextRoot === undefined) {
    return true
  }
  if (nextRoot === (root + 5) % 12 || nextRoot === (root + 1) % 12 || nextRoot === (root + 2) % 12) {
    return true
  }
  if (isSameSymbol(chord, next)) {
    return true
  }
  const nextAnalysis = analyze(nameOf(next), key)
  return Boolean(nextAnalysis && appliedDominantFunction(nextAnalysis, next, key))
}

/** The target's diatonic chord in the key, e.g. 'Am' for E in C: the triad built on the degree a fifth below. */
const targetChordName = (chord: ChordSymbol, key: Scale) => {
  const root = pitchOf(chord.root) ?? 0
  const target = (root + 5) % 12
  const hasMinorThird = isInKey(spellInKey(target + 3, key), key)
  const hasPerfectFifth = isInKey(spellInKey(target + 7, key), key)
  const quality = !hasMinorThird ? '' : hasPerfectFifth ? 'm' : 'dim'
  return `${spellInKey(target, key)}${quality}`
}

type MarkerVerdict =
  | { kind: 'unanalyzable' }
  | { kind: 'explained' }
  | {
      kind: 'flagged'
      reason: ChordCheckReason
      chordFunction: ChordFunction
      resolution?: { target: string; next: string }
    }

/**
 * The marker's verdict on one occurrence in one key (Bowie Q1). Exported for its tests: the minor
 * battery must hold per chord, not just survive the chart-level gates.
 */
export const explainForMarker = (
  chords: readonly ChordSymbol[],
  index: number,
  key: Scale,
  criteria: PlausibilityCriteria
): MarkerVerdict => {
  const chord = chords[index]
  const analysis = analyze(nameOf(chord), key)
  if (!analysis) {
    return { kind: 'unanalyzable' }
  }
  const variant = chord.variant ?? ''
  if (
    !isNonDiatonic(analysis) ||
    isThirdlessInKey(chord.root, variant, key) ||
    isMarkerBluesSeven(chord.root, variant, key) ||
    isMinorFlatTwo(chord.root, variant, key)
  ) {
    return { kind: 'explained' }
  }
  const applied = appliedDominantFunction(analysis, chord, key)
  if (applied) {
    if (isResolved(chords, index, key)) {
      return { kind: 'explained' }
    }
    const next = chords[index + 1]
    return {
      kind: 'flagged',
      reason: 'unresolved-applied-dominant',
      chordFunction: applied,
      resolution: { target: targetChordName(chord, key), next: next ? nameOf(next) : '' },
    }
  }
  const isWhitelisted = (criteria.allowedFunctions ?? []).includes(analysis.chordFunction)
  if (analysis.borrowing !== null) {
    // Parallel-mode borrowings and tritone substitutions: the whitelist always wins (Bowie Q1)
    return isWhitelisted
      ? { kind: 'explained' }
      : { kind: 'flagged', reason: 'unexplained-borrowing', chordFunction: analysis.chordFunction }
  }
  return isWhitelisted
    ? { kind: 'explained' }
    : { kind: 'flagged', reason: 'chromatic', chordFunction: analysis.chordFunction }
}

/**
 * Every occurrence worth marking, after Bowie's chart-level gates. Each gate yields zero markers:
 * no candidate key; fewer than `minAnalyzableChords` analyzable occurrences; a flagged share over
 * 0.25; more than 3 distinct flagged symbols. When the gates pass, every occurrence of a flagged
 * symbol is marked: fixing the E in chorus 1 must not hide the same E in chorus 3.
 *
 * With several candidate keys (an inferred tie), an occurrence is flagged only when it is unexplained
 * in every one of them (Bowie Q4).
 */
export const findChordsToCheck = (
  chords: readonly ChordSymbol[],
  options: { keys: readonly Scale[]; inferred: boolean; criteria: PlausibilityCriteria }
): ChordsToCheckResult => {
  const { keys, inferred, criteria } = options
  const keyNames = keys.map(keyNameOf)
  if (keys.length === 0) {
    return { keys: [], flaggedRatio: null, chords: [] }
  }

  let analyzable = 0
  const flagged: ChordToCheck[] = []
  for (const [index] of chords.entries()) {
    const verdicts = keys.map(key => explainForMarker(chords, index, key, criteria))
    const [first] = verdicts
    if (first.kind === 'unanalyzable') {
      continue
    }
    analyzable += 1
    if (first.kind !== 'flagged' || !verdicts.every(verdict => verdict.kind === 'flagged')) {
      continue
    }
    flagged.push({
      index,
      reason: first.reason,
      chordFunction: first.chordFunction,
      keys: keyNames,
      inferred,
      ...(first.resolution ? { resolution: first.resolution } : {}),
    })
  }

  const flaggedRatio = analyzable === 0 ? null : flagged.length / analyzable
  const floor = criteria.minAnalyzableChords ?? 0
  if (analyzable === 0 || analyzable < floor || flaggedRatio === null || flaggedRatio > markerGates.maxFlaggedRatio) {
    return { keys: keyNames, flaggedRatio, chords: [] }
  }

  const symbolKey = (item: ChordToCheck) =>
    `${pitchOf(chords[item.index].root)}|${chords[item.index].variant ?? ''}|${item.chordFunction}`
  const distinct = new Map<string, ChordToCheck>()
  for (const item of flagged) {
    if (!distinct.has(symbolKey(item))) {
      distinct.set(symbolKey(item), item)
    }
  }
  if (distinct.size > markerGates.maxDistinctFlagged) {
    return { keys: keyNames, flaggedRatio, chords: [] }
  }

  // Every occurrence of each flagged symbol, carrying that symbol's verdict: a chromatic chord or an
  // unexplained borrowing is wrong wherever it appears. An unresolved applied dominant depends on what
  // follows, so it is marked only where it fails in its own context (Bowie, #2423 correction 2): the E → Am
  // in the verse stays clean when the chorus has E → C. Its `next` is re-derived per place.
  const flaggedHere = new Set(flagged.map(item => item.index))
  const marked = chords.flatMap((chord, index) => {
    const match = [...distinct.values()].find(item => isSameSymbol(chords[item.index], chord))
    if (!match || (match.reason === 'unresolved-applied-dominant' && !flaggedHere.has(index))) {
      return []
    }
    const next = chords[index + 1]
    const resolution = match.resolution ? { ...match.resolution, next: next ? nameOf(next) : '' } : undefined
    return [{ ...match, index, ...(resolution ? { resolution } : {}) }]
  })
  return { keys: keyNames, flaggedRatio, chords: marked }
}

/**
 * The candidate keys for the marker (Bowie Q4).
 *
 * - **Stated key:** the single candidate — unless a confident inference names a different key that is
 *   neither its relative nor its parallel, in which case one of the two is wrong and a mis-stated key
 *   flags correct chords, so there are no candidates (mark nothing).
 * - **No stated key:** the stored inference when the evidence decides one (`inferKeyToStore`),
 *   otherwise every key tied with the top on chord-tone score and tonic-at-the-ends.
 */
export const chordCheckKeys = (
  chords: readonly ChordSymbol[],
  stated?: Scale
): { keys: Scale[]; inferred: boolean } => {
  const parsed = chords.flatMap(chord => {
    const value = fromName(nameOf(chord).trim())
    return value ? [value] : []
  })
  const inference = inferKeyCandidates(parsed)
  if (stated) {
    const disagrees =
      inference.decided !== undefined &&
      inference.fit >= confidentFit &&
      !isRelativeOrParallel(inference.decided, stated)
    return { keys: disagrees ? [] : [stated], inferred: false }
  }
  return { keys: inference.decided ? [inference.decided] : inference.candidates, inferred: true }
}

/**
 * Provisional (approved by Brian, flagged for Bowie on the PR): an inference is "confident" when the
 * evidence decides a key AND at least 90 % of the chart's chord tones fit it. A top-versus-runner-up
 * margin can't serve, because the runner-up is almost always the relative key, which ties on score.
 */
const confidentFit = 0.9

const isMinorType = (key: Scale) => key.type === 'minor'

/** Same tonic (parallel), or major and minor a minor third apart (relative). */
const isRelativeOrParallel = (a: Scale, b: Scale) => {
  const aRoot = pitchOf(a.root)
  const bRoot = pitchOf(b.root)
  if (aRoot === undefined || bRoot === undefined) {
    return false
  }
  if (aRoot === bRoot) {
    return true
  }
  if (isMinorType(a) === isMinorType(b)) {
    return false
  }
  const [majorRoot, minorRoot] = isMinorType(a) ? [bRoot, aRoot] : [aRoot, bRoot]
  return (majorRoot + 9) % 12 === minorRoot
}

/**
 * What the chord node carries for the marker (spec §2). Written once at import; `flagged` is the
 * fingerprint the marker checks, so an edit to the chord retires it without touching this field.
 */
export interface ChordCheckHint {
  reason: ChordCheckReason
  chordFunction: string
  keys: string[]
  inferred: boolean
  resolution?: { target: string; next: string }
  flagged: { root: string; variant: string }
}

/** What the chart said about its key: nothing, a key the chords fit, or a key they don't. */
export type KeyStatement = { kind: 'none' } | { kind: 'stated'; scale: Scale } | { kind: 'unfit' }

/** The sketch shape both importers produce, declared structurally (see `sketchToNodes` in slate-utils). */
interface HintableChordSegment {
  chord: ChordSymbol & { checkHint?: ChordCheckHint }
}
type HintableSegment = { text: string } | HintableChordSegment
interface HintableSketch {
  sections: { lines: ({ segments: HintableSegment[] } | { tab: unknown })[] }[]
}

const isChordSegment = (segment: HintableSegment): segment is HintableChordSegment => 'chord' in segment

/**
 * The sketch with a `checkHint` on every chord segment worth a second listen, for `sketchToNodes` to
 * write onto the chord node. Both importers call this — the paste path in the browser, the research
 * path in the task Lambda — so there is one copy of the decision.
 *
 * A chart that states a key its chords don't fit gets no hints: the stated key and the chords disagree,
 * and one of them is wrong (Bowie Q4). Returns the sketch unchanged when nothing is marked.
 */
export const withCheckHints = <T extends HintableSketch>(sketch: T, keyStatement: KeyStatement): T => {
  if (keyStatement.kind === 'unfit') {
    return sketch
  }
  const segments = sketch.sections.flatMap(section =>
    section.lines.flatMap(block => ('segments' in block ? block.segments.filter(isChordSegment) : []))
  )
  const chords = segments.map(segment => segment.chord)
  const { keys, inferred } = chordCheckKeys(chords, keyStatement.kind === 'stated' ? keyStatement.scale : undefined)
  const marked = findChordsToCheck(chords, { keys, inferred, criteria: bowieCriteria }).chords
  if (marked.length === 0) {
    return sketch
  }
  const hints = new Map<HintableChordSegment, ChordCheckHint>(
    marked.map(item => [
      segments[item.index],
      {
        reason: item.reason,
        chordFunction: item.chordFunction,
        keys: item.keys,
        inferred: item.inferred,
        ...(item.resolution ? { resolution: item.resolution } : {}),
        flagged: { root: chords[item.index].root, variant: chords[item.index].variant ?? '' },
      },
    ])
  )
  const hinted = (segment: HintableSegment): HintableSegment => {
    const checkHint = isChordSegment(segment) ? hints.get(segment) : undefined
    return checkHint && isChordSegment(segment) ? { ...segment, chord: { ...segment.chord, checkHint } } : segment
  }
  return {
    ...sketch,
    sections: sketch.sections.map(section => ({
      ...section,
      lines: section.lines.map(block =>
        'segments' in block ? { ...block, segments: block.segments.map(hinted) } : block
      ),
    })),
  }
}
